import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { WaveIcon, Sparkle } from "@/components/landing/Decorations";
import { LayoutDashboard, FileAudio, Mic2, Code2, BarChart3, Wallet, Users, Settings, LogOut, Menu, X, Sparkles, Shield, LifeBuoy, AlertTriangle, DollarSign, Plug2 } from "lucide-react";
import UpgradeModal from "./UpgradeModal";

const NAV = [
    {key: "overview", to: "/dashboard", end: true, label: "Overview", icon: LayoutDashboard},
    {key: "articles", to: "/dashboard/articles", label: "Articles", icon: FileAudio},
    {key: "voices", to: "/dashboard/voices", label: "Voices", icon: Mic2},
    {key: "embed", to: "/dashboard/embed", label: "Embed", icon: Code2},
    {key: "analytics", to: "/dashboard/analytics", label: "Analytics", icon: BarChart3},
    {key: "billing", to: "/dashboard/billing", label: "Billing", icon: Wallet},
    {key: "monetize", to: "/dashboard/monetize", label: "Monetize", icon: DollarSign},
    {key: "api", to: "/dashboard/api", label: "API & Plugins", icon: Plug2},
    {key: "support", to: "/dashboard/support", label: "Support", icon: LifeBuoy},
    {key: "team", to: "/dashboard/team", label: "Team", icon: Users},
    {key: "settings", to: "/dashboard/settings", label: "Settings", icon: Settings},
];

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const initials = (user?.name || user?.email || "U").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

    // Renewal banner: show 3 days before expiry for paid plans
    const renewsAt = user?.plan_renews_at;
    const daysLeft = renewsAt ? Math.ceil((new Date(renewsAt) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const showRenewalBanner = user?.plan && user.plan !== "free" && daysLeft !== null && daysLeft <= 3 && daysLeft >= 0;

    return (
        <div className="min-h-screen bg-cream-100 flex">
            <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)}/>
            {/* Sidebar */}
            <aside data-testid="dashboard-sidebar" className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-[260px] bg-white border-r border-cream-300 flex flex-col p-4 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
                <Link to="/dashboard" className="flex items-center gap-2.5 px-3 py-2 mb-4">
                    <WaveIcon color="#FF5B3E" className="w-6 h-4"/>
                    <span className="font-display text-[20px] font-semibold tracking-tight text-ink">Audo</span>
                </Link>

                {/* Plan card */}
                <div className="mb-4 mx-1 bg-cream-100 rounded-2xl p-3.5 border border-cream-300 relative overflow-hidden">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Sparkles size={12} className="text-coral"/>
                        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Current plan</span>
                    </div>
                    <p className="font-display text-[18px] font-semibold tracking-tight text-ink capitalize">{user?.plan || "starter"}</p>
                    <button onClick={() => setUpgradeOpen(true)} className="text-[11.5px] text-coral hover:underline font-semibold">Upgrade →</button>
                    <Sparkle color="#FFD23F" className="absolute -top-1 -right-1 w-6 h-6 opacity-70"/>
                </div>

                <nav className="flex-1 space-y-1">
                    {NAV.map(item => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.key}
                                to={item.to}
                                end={item.end}
                                data-testid={`sidebar-${item.key}`}
                                onClick={() => setOpen(false)}
                                className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] font-medium transition-colors ${isActive ? "bg-ink text-cream-100" : "text-ink hover:bg-cream-100"}`}
                            >
                                <Icon size={16}/>
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* Admin link for admins */}
                {user?.role === "admin" && (
                    <Link to="/admin"
                        className="mt-2 mx-1 flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold bg-coral/10 text-coral hover:bg-coral hover:text-white transition-colors">
                        <Shield size={14}/> Admin panel
                    </Link>
                )}

                {/* User card */}
                <div className="mt-3 pt-3 border-t border-cream-300">
                    <div className="flex items-center gap-3 px-2 py-2 rounded-xl">
                        {user?.picture ? (
                            <img src={user.picture} alt="" className="w-9 h-9 rounded-full"/>
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-coral text-white flex items-center justify-center text-[13px] font-bold">{initials}</div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-ink truncate">{user?.name}</p>
                            <p className="text-[11px] text-ink-muted truncate">{user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="sidebar-logout"
                        className="mt-1 w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium text-ink-muted hover:text-coral hover:bg-cream-100 transition-colors"
                    >
                        <LogOut size={14}/> Sign out
                    </button>
                </div>
            </aside>

            {/* Backdrop */}
            {open && <div onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 z-30 bg-black/40"/>}

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="lg:hidden sticky top-0 z-20 bg-cream-100/90 backdrop-blur border-b border-cream-300 px-4 py-3 flex items-center gap-3">
                    <button onClick={() => setOpen(!open)} data-testid="dashboard-mobile-toggle" className="w-10 h-10 rounded-xl bg-white border border-cream-300 flex items-center justify-center">
                        {open ? <X size={16}/> : <Menu size={16}/>}
                    </button>
                    <Link to="/dashboard" className="flex items-center gap-2">
                        <WaveIcon color="#FF5B3E" className="w-5 h-3.5"/>
                        <span className="font-display text-[17px] font-semibold tracking-tight">Audo</span>
                    </Link>
                </header>
                <main className="flex-1 p-5 sm:p-8 lg:p-10">
                    {showRenewalBanner && (
                        <div className="mb-6 flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5">
                            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0"/>
                            <p className="text-[13px] text-amber-900 flex-1">
                                <strong>Your {user.plan} plan</strong> {daysLeft === 0 ? "expires today" : `expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`} ({new Date(renewsAt).toLocaleDateString("en-BD")}).
                            </p>
                            <button onClick={() => setUpgradeOpen(true)} className="text-[12px] font-semibold text-amber-900 bg-amber-200 hover:bg-amber-300 px-3 py-1.5 rounded-full transition whitespace-nowrap">
                                Renew now →
                            </button>
                        </div>
                    )}
                    <Outlet/>
                </main>
            </div>
        </div>
    );
}

// Page header used inside each dashboard page
export const PageHeader = ({ title, subtitle, action, eyebrow }) => (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
            {eyebrow && <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-2">{eyebrow}</p>}
            <h1 className="h-display text-[40px] sm:text-[52px] lg:text-[64px] font-medium tracking-tight text-ink leading-[1.05]">
                {title}
            </h1>
            {subtitle && <p className="mt-3 text-[15px] text-ink-muted max-w-[560px]">{subtitle}</p>}
        </div>
        {action}
    </div>
);
